<template>
  <div class="od-photo-input">
    <div class="od-photo-grid">
      <div
        v-for="p in photos"
        :key="p.id"
        class="od-photo-tile"
        :class="{ 'od-photo-tile--err': p.status === 'error' }"
      >
        <img :src="p.previewUrl" :alt="p.name"/>
        <div v-if="p.status === 'uploading'" class="od-photo-veil">
          <i class="fa fa-circle-o-notch fa-spin"></i>
        </div>
        <div v-else-if="p.status === 'error'" class="od-photo-veil od-photo-veil--err" :title="p.error">
          <i class="fa fa-exclamation-triangle"></i>
        </div>
        <button type="button" class="od-photo-remove" title="Remove photo" @click="removePhoto(p)">&times;</button>
      </div>

      <label v-if="photos.length < max" class="od-photo-add">
        <input
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          multiple
          @change="onPick"
        />
        <i class="fa fa-camera"></i>
        <span>Add photos</span>
      </label>
    </div>
    <div v-if="pickError" class="od-photo-error">{{ pickError }}</div>
    <div v-else class="od-photo-hint">JPEG, PNG, GIF, or WebP. Up to {{ max }} photos, 20 MB each.</div>
  </div>
</template>

<script>
// FI-1812 shared photo picker + uploader. the parent supplies a presign
// function (public-slug or authed-tenant flavor); each picked file is
// presigned and POSTed straight to s3 so slow uploads happen while the
// user keeps typing. emits:
//   input  -> array of staged s3 keys for the successfully uploaded photos
//   busy   -> true while any upload is in flight (parents gate submit on it)
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
const MAX_BYTES = 20 * 1024 * 1024

export default {
  name: 'od_photo_input',
  props: {
    // async (file) => {key, upload: {url, fields}} — the unwrapped
    // presign response from either presign api
    presign: { type: Function, required: true },
    max: { type: Number, default: 10 },
  },
  data() {
    return {
      photos: [],
      pickError: '',
      nextId: 1,
    }
  },
  beforeDestroy() {
    this.photos.forEach(p => URL.revokeObjectURL(p.previewUrl))
  },
  methods: {
    onPick(evt) {
      this.pickError = ''
      const files = Array.from(evt.target.files || [])
      evt.target.value = ''
      for (const file of files) {
        if (this.photos.length >= this.max) {
          this.pickError = 'Up to ' + this.max + ' photos per event.'
          break
        }
        if (ALLOWED_TYPES.indexOf(file.type) === -1) {
          this.pickError = 'Photos only: JPEG, PNG, GIF, or WebP.'
          continue
        }
        if (file.size > MAX_BYTES) {
          this.pickError = '"' + file.name + '" is over 20 MB.'
          continue
        }
        const photo = {
          id: this.nextId++,
          name: file.name,
          previewUrl: URL.createObjectURL(file),
          status: 'uploading',
          key: null,
          error: '',
        }
        this.photos.push(photo)
        this.uploadOne(photo, file)
      }
      this.emitState()
    },

    async uploadOne(photo, file) {
      try {
        const presigned = await this.presign(file)
        if (!presigned || !presigned.upload || !presigned.upload.url) {
          throw new Error('no upload target')
        }
        const fd = new FormData()
        Object.entries(presigned.upload.fields || {}).forEach(([k, v]) => fd.append(k, v))
        fd.append('file', file)
        const res = await fetch(presigned.upload.url, { method: 'POST', body: fd })
        if (!res.ok) throw new Error('upload failed (' + res.status + ')')
        photo.key = presigned.key
        photo.status = 'done'
      } catch (e) {
        photo.status = 'error'
        photo.error = (e && e.message) || 'upload failed'
        // api errors are a flat {message} body
        const body = e && e.response && e.response.data
        if (body && body.message) photo.error = body.message
      }
      this.emitState()
    },

    removePhoto(photo) {
      URL.revokeObjectURL(photo.previewUrl)
      this.photos = this.photos.filter(p => p.id !== photo.id)
      this.pickError = ''
      this.emitState()
    },

    clear() {
      this.photos.forEach(p => URL.revokeObjectURL(p.previewUrl))
      this.photos = []
      this.pickError = ''
      this.emitState()
    },

    emitState() {
      this.$emit('input', this.photos.filter(p => p.status === 'done' && p.key).map(p => p.key))
      this.$emit('busy', this.photos.some(p => p.status === 'uploading'))
    },
  },
}
</script>

<style scoped>
.od-photo-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.od-photo-tile {
  position: relative;
  width: 76px;
  height: 76px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #d6dde6;
  background: #f8fafc;
}
.od-photo-tile--err { border-color: #e4a2a2; }
.od-photo-tile img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.od-photo-veil {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(248, 250, 252, 0.72);
  color: var(--fi-rail-accent, #4682B4);
  font-size: 18px;
}
.od-photo-veil--err { color: #b04343; }
.od-photo-remove {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: rgba(29, 29, 31, 0.65);
  color: #fff;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}
.od-photo-remove:hover { background: rgba(29, 29, 31, 0.85); }
.od-photo-add {
  width: 76px;
  height: 76px;
  border: 1.5px dashed #b8c8da;
  border-radius: 8px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  color: #4b5563;
  font-size: 11px;
  cursor: pointer;
  margin: 0;
  text-align: center;
}
.od-photo-add:hover {
  border-color: var(--fi-rail-accent, #4682B4);
  color: var(--fi-rail-accent, #4682B4);
  background: #eef4fb;
}
.od-photo-add input[type="file"] {
  display: none;
}
.od-photo-add .fa {
  font-size: 17px;
}
.od-photo-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #8a94a3;
}
.od-photo-error {
  margin-top: 6px;
  font-size: 12px;
  color: #b04343;
}
</style>
